import { useEffect, useRef, useState } from 'react';
import parseZone from 'helpers/parseZone';
import { FormProvider, useForm } from 'react-hook-form';
import BookingInfoItem from 'components/messaging/BookingInfoItem';
import { dollarsWithCentsWithSymbol } from 'helpers/currency';
import OwnerDeclineCancelContent from 'components/modals/content/OwnerDeclineCancelContent';
import { ownerEarnings } from 'stacks/messaging/helpers';
import { useBreakpoint } from 'components/BreakpointProvider';
import { useRouter } from 'next/router';
import getServerSidePropsImported from 'components/checkout/getServerSideProps';
import OwnerPreApproveContent from 'components/modals/content/OwnerPreApproveContent';
import { useBookingTrip } from 'components/booking/queryHooks';
import {
  useDeleteBookingMutation,
  useApproveBookingMutation,
  usePreApproveBookingMutation,
} from 'components/booking/mutationHooks';
import { useCloseModal } from 'components/remodal/v2/hooks';
import { Booking } from 'swagger/models';
import { getAsString } from 'helpers';
import useRudderStackTrack from 'hooks/useRudderStackTrack';

interface BaselineProps {
  booking: Booking;
  onSuccess?: () => void;
}

type ActionType = 'cancel' | 'approve' | 'decline' | 'pre-approve' | 'special-offer';

interface NonCancelProps extends BaselineProps {
  actionType: Exclude<ActionType, 'cancel' | 'decline'>;
}

interface CancelProps extends BaselineProps {
  actionType: Extract<'cancel' | 'decline', ActionType>;
  openEditBookingModal: () => void;
}

const OwnerBookingActionsModal = (props: NonCancelProps | CancelProps) => {
  const { actionType: actionTypeModal, onSuccess, booking: bookingModal } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const closeModal = useCloseModal();
  const router = useRouter();
  const [bookingActionDone, setBookingActionDone] = useState(false);
  const { bookingTrip } = useBookingTrip();
  const preApproveBookingMutation = usePreApproveBookingMutation();

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  const methods = useForm({
    mode: 'onChange',
  });
  const { handleSubmit } = methods;

  const deleteBookingMutation = useDeleteBookingMutation({
    onSuccess() {
      if (isFramed) {
        setBookingActionDone(true);
      }
      if (onSuccess) {
        onSuccess();
      }
      setIsSubmitting(false);
    },
    onError(error) {
      console.error(error);
      setIsSubmitting(false);
    },
  });

  const approveBookingMutation = useApproveBookingMutation({
    onSuccess() {
      if (isFramed) {
        setBookingActionDone(true);
      }
      onSuccess?.();
      setIsSubmitting(false);
    },
    onError() {
      closeModal();
      setIsSubmitting(false);
    },
  });
  const loading =
    approveBookingMutation.isLoading || deleteBookingMutation.isLoading || preApproveBookingMutation.isLoading;

  const params = router.query;
  const isFramed = router.asPath.includes('owner_action');

  const booking = isFramed ? bookingTrip : bookingModal;
  const action = getAsString(params?.action) as ActionType;
  const actionType = isFramed ? action : actionTypeModal;
  const showBookingInfo = actionType === 'pre-approve' || actionType === 'approve';

  const {
    trip_start,
    trip_finish,
    state,
    package_type,
    package_price,
    date_price_adjustment,
    insurance_type,
    owner_payout,
    // TODO @vstlouis model needs inspecting
    // @ts-ignore
    owner_earning,
    passengers,
  } = booking;

  const contentRef = useRef();

  const modalIdReference = () => {
    if (actionType === 'decline') {
      return `#js-owner-decline-booking-modal-${booking.id}`;
    }
    if (actionType === 'cancel') {
      return `#js-owner-cancel-booking-modal-${booking.id}`;
    }
    if (actionType === 'approve') {
      return `#js-approve-modal-${booking.id}`;
    }
  };

  const rudderTrack = useRudderStackTrack();
  const trackAction = (act, reason) => {
    rudderTrack('booking_action_owner', {
      booking: booking.id,
      action: act,
      reason,
      location: 'owner-booking-actions-modal',
    });
  };

  const processFormData = async (formValues) => {
    if (loading) {
      return;
    }

    const { reason_text, reason_text_other, category, pickup_instructions, special_offer_price } = formValues;
    trackAction(actionType, reason_text);
    if (actionType === 'decline') {
      setIsSubmitting(true);
      deleteBookingMutation.mutate({
        bookingId: booking.id,
        reasonText: reason_text,
        category,
      });
    } else if (actionType === 'cancel') {
      setIsSubmitting(true);
      let reason = reason_text;
      if (reason_text === 'Other') {
        reason = reason_text_other;
      }
      deleteBookingMutation.mutate({
        bookingId: booking.id,
        reasonText: reason,
      });
    } else if (actionType === 'pre-approve') {
      preApproveBookingMutation.mutate({
        bookingId: booking.id,
        body: {
          pickup_instructions,
        },
      });
    } else if (actionType === 'special-offer') {
      const special_offer = parseInt(special_offer_price, 10) * 100;
      preApproveBookingMutation.mutate({
        bookingId: booking.id,
        body: {
          pickup_instructions,
          // TODO @vstlouis model needs inspecting
          // @ts-ignore
          special_offer_price: special_offer,
        },
      });
    } else if (actionType === 'approve') {
      setIsSubmitting(true);
      approveBookingMutation.mutate({
        bookingId: booking.id,
        body: {
          pickup_instructions,
        },
      });
    }
    setBookingActionDone(true);
  };

  const handleCloseModal = () => {
    closeModal?.();
    setBookingActionDone(false);
    if (window?.parent?.remodalClose) {
      window?.parent?.remodalClose(modalIdReference());
      window?.parent?.location?.reload();
    }
  };

  const diffHours = parseZone(trip_finish).diff(parseZone(trip_start), 'hour', true);

  const infoItems = [
    {
      label: 'Date',
      value: `${parseZone(trip_start).format('MMM DD, YYYY')}`,
    },
    {
      label: 'Start time',
      value: `${parseZone(trip_start).format('h:mm A')}`,
    },
    {
      label: 'Duration',
      value: diffHours >= 24 ? `${Math.ceil(diffHours / 24)} days` : `${diffHours} hours`,
    },
    {
      label: 'Captain',
      value: package_type === 'captained' ? 'Captained' : 'No captain',
    },
    {
      label: 'Passengers',
      value: passengers,
    },
  ].filter((b) => !!b.value);

  const resizeIframe = (extraSpace = null) => {
    // @ts-ignore
    const modalHeight = (contentRef?.current?.offsetHeight || 0) + 30;
    if (isFramed && window.parent && window.parent.resizeIframeModal) {
      const space = extraSpace || 0;
      window.parent.resizeIframeModal(modalIdReference(), modalHeight + space);
    } else {
      const windowHeight = window.innerHeight;
      const animateHeight = 100 - (modalHeight * 100) / windowHeight;
      const extra_space = extraSpace || 0;
      const space = extra_space + (smallScreen ? 0 : 70);
      document.body.setAttribute(
        'style',
        `--modal-height-offset: ${animateHeight}vh; --modal-content-height: ${modalHeight + space}px`
      );
    }
  };

  const content = () => {
    if (actionType === 'decline') {
      const editAction = () => {
        if (isFramed) {
          // TODO @vstlouis model needs inspecting
          // @ts-ignore
          window.parent.location.href = `${booking.details_url}?open_edit_modal=true`;
          return;
        }
        // eslint-disable-next-line react/destructuring-assignment
        if (props.actionType === 'decline') return props.openEditBookingModal;
      };

      return (
        <OwnerDeclineCancelContent
          isSubmitting={isSubmitting}
          openEditBookingModal={editAction}
          hideModal={handleCloseModal}
          booking={booking}
          resizeIframe={resizeIframe}
          isFramed={isFramed}
        />
      );
    }
    if (actionType === 'cancel') {
      return (
        <OwnerDeclineCancelContent
          isSubmitting={isSubmitting}
          // eslint-disable-next-line react/destructuring-assignment
          openEditBookingModal={props.actionType === 'cancel' ? props.openEditBookingModal : null}
          hideModal={handleCloseModal}
          isCancel
          booking={booking}
          resizeIframe={resizeIframe}
          isFramed={isFramed}
        />
      );
    }
    if (actionType === 'special-offer') {
      return (
        <OwnerPreApproveContent
          approve={false}
          specialOffer
          hideModal={handleCloseModal}
          insuranceType={insurance_type}
          booking={booking}
        />
      );
    }
    if (actionType === 'approve') {
      return (
        <OwnerPreApproveContent
          approve
          specialOffer={false}
          hideModal={handleCloseModal}
          insuranceType={insurance_type}
          booking={booking}
        />
      );
    }
  };

  useEffect(() => {
    resizeIframe();
  }, [contentRef]);

  return (
    <div className="w-100" ref={contentRef}>
      {bookingActionDone ? (
        <div className={`u-p1_5 ${!isFramed ? 'u-sm-p0' : ''}`}>
          <div style={{ height: '45vh' }} className="u-flex u-flexJustifyCenter u-flexAlignItemsStart u-textCenter">
            <div className="Grid">
              <div className="Grid-cell Grid-cell u-flex u-flexAlignItemsCenter u-flexCol">
                <img src="/images/done.svg" alt="Done" className="u-mt1 u-mb2" />
                <h2 className="u-fsXl u-textSemiBold u-mb1">
                  {actionType === 'special-offer'
                    ? 'You have Sent a special offer for this booking'
                    : `This booking has been ${actionType === 'cancel' ? 'cancele' : actionType}d`}
                </h2>
                <button
                  className="Button Button--primary Button--sticky u-textBold u-textUppercase u-flex u-flexAlignItemsCenter u-flexJustifyCenter u-mb1"
                  type="button"
                  onClick={() => handleCloseModal()}
                  style={{ minWidth: 175 }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={`u-p1_5 ${!isFramed ? 'u-sm-p0' : ''}`}>
          <div className="Grid zzz">
            <div className="Grid-cell">
              <div className="u-mb1">
                <h1
                  className={`u-fsXl u-textSemiBold u-mb1 ${
                    isFramed ? 'isFramedTray' : ''
                  } remodalTrayTitleMobile u-textCapitalizeFirstLetter`}
                >
                  {actionType === 'special-offer' ? 'Send a special offer' : `${actionType} booking`}
                </h1>
                {actionType === 'decline' && (
                  <p className={`u-mb1 ${isFramed ? 'isFramedTray' : ''} remodalTraySpacingMobile`}>
                    You can always edit the booking request. If you need additional help please email&nbsp;
                    <a className="u-textOwners" href="mailto:contact@boatsetter.com">
                      contact@boatsetter.com
                    </a>{' '}
                    for assistance.
                  </p>
                )}

                {actionType === 'cancel' && (
                  <p className={`u-mb1 ${isFramed ? 'isFramedTray' : ''} remodalTraySpacingMobile`}>
                    You can always edit the booking. If you need additional help, please email{' '}
                    <a className="u-textOwners" href="mailto:contact@boatsetter.com">
                      contact@boatsetter.com
                    </a>{' '}
                    for assistance.
                  </p>
                )}
                {actionType === 'pre-approve' && booking?.renter?.first_name && (
                  <p className={`u-mb1 ${isFramed ? 'isFramedTray' : ''} remodalTraySpacingMobile`}>
                    If you are okay with {booking?.renter?.first_name}’s booking request, you can pre-approve it. If
                    they choose to book, their request will be automatically approved.
                  </p>
                )}
              </div>
            </div>
          </div>

          {showBookingInfo && (
            <>
              <div
                className={`${isFramed ? 'isFramedTray' : ''} u-fsBase u-textBold u-mb1 ${
                  actionType === 'pre-approve' && booking?.renter?.first_name ? '' : 'remodalTraySpacingMobile'
                } `}
              >
                Details
              </div>
              <div className="Grid Grid--withGutterWide Grid-sm--withGutterWideNoMargin Grid--alignMiddle">
                <div className="Grid-cell Grid-cell-sm--noGutter u-md-size1of2 u-lg-size1of2 u-sm-mb1">
                  {infoItems.map((item) => (
                    <BookingInfoItem key={item.label} label={item.label} value={item.value} />
                  ))}
                </div>

                <div
                  className={`Grid-cell Grid-cell-sm--noGutter ${
                    showBookingInfo ? 'u-md-size1of2 u-lg-size1of2' : ''
                  } u-flexAlignItemsCenter`}
                >
                  <div className="Panel Panel--light Panel--semiCompact u-mb2 u-block Panel--border Panel--primary Panel--stretch">
                    <div className="Grid Grid--alignMiddle u-mb05">
                      <div className="Grid-cell u-size1of3">
                        <div className="u-fsSmMd">Boat price</div>
                      </div>
                      <div className="Grid-cell u-size2of3">
                        <div className="u-fsSmMd u-textRight u-textSemiBold">
                          {dollarsWithCentsWithSymbol(package_price + date_price_adjustment)}
                        </div>
                      </div>
                    </div>

                    {booking.owner_pricing?.captain_price > 0 && (
                      <div className="Grid Grid--alignMiddle u-mb05">
                        <div className="Grid-cell u-size1of3">
                          <div className="u-fsSmMd">Captain price</div>
                        </div>
                        <div className="Grid-cell u-size2of3">
                          <div className="u-fsSmMd u-textRight u-textSemiBold">
                            {dollarsWithCentsWithSymbol(booking.owner_pricing?.captain_price)}
                          </div>
                        </div>
                      </div>
                    )}

                    {booking.renter_add_on_total > 0 && (
                      <div className="Grid Grid--alignMiddle u-mb05">
                        <div className="Grid-cell u-size1of3">
                          <div className="u-fsSmMd">Add-ons</div>
                        </div>
                        <div className="Grid-cell u-size2of3">
                          <div className="u-fsSmMd u-textRight u-textSemiBold">
                            {dollarsWithCentsWithSymbol(booking.renter_add_on_total)}
                          </div>
                        </div>
                      </div>
                    )}

                    {booking.owner_listing_fee > 0 && (
                      <div className="Grid Grid--alignMiddle u-mb05">
                        <div className="Grid-cell u-size1of3">
                          <div className="u-fsSmMd">Listing fee</div>
                        </div>
                        <div className="Grid-cell u-size2of3">
                          <div className="u-fsSmMd u-textRight u-textSemiBold">
                            -{dollarsWithCentsWithSymbol(booking.owner_listing_fee)}
                          </div>
                        </div>
                      </div>
                    )}

                    {booking.owner_insurance_fee > 0 && (
                      <div className="Grid Grid--alignMiddle u-mb05">
                        <div className="Grid-cell u-size1of3">
                          <div className="u-fsSmMd">Insurance</div>
                        </div>
                        <div className="Grid-cell u-size2of3">
                          <div className="u-fsSmMd u-textRight u-textSemiBold">
                            -{dollarsWithCentsWithSymbol(booking.owner_insurance_fee)}
                          </div>
                        </div>
                      </div>
                    )}

                    {!booking.pay_captain_at_dock ? (
                      <div className="Grid Grid--alignMiddle u-mb05">
                        <div className="Grid-cell u-size1of3">
                          <div className="u-fsSmMd u-textBold">You earn</div>
                        </div>
                        <div className="Grid-cell u-size2of3">
                          <div className="u-fsSmMd u-textRight u-textBold u-textSuccess">
                            {dollarsWithCentsWithSymbol(booking.owner_pricing?.earnings_total)}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="Grid Grid--alignMiddle u-mb05">
                        <div className="Grid-cell u-size1of3">
                          <div className="u-fsSmMd u-textBold">You earn</div>
                        </div>
                        <div className="Grid-cell u-size2of3">
                          <div className="u-fsSmMd u-textRight u-textBold u-textSuccess">
                            {dollarsWithCentsWithSymbol(ownerEarnings(state, owner_earning, owner_payout))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <hr className="Divider u-mt2 u-mb2" />
            </>
          )}

          <div className="Grid">
            <div className="Grid-cell">
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(processFormData)}>{content()}</form>
              </FormProvider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const getServerSideProps = getServerSidePropsImported;

export default OwnerBookingActionsModal;
